import PropTypes from 'prop-types';
import { Form } from 'formik';
import * as Yup from 'yup';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import MLink from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import RadioGroupField from '../forms/radio-group-field';
import TextField from '../forms/text-field';
import TextAreaField from '../forms/text-area-field';
import FileField from '../forms/file-field';
import ImageUpload from '../image-upload';
import CityField from '../forms/city-field';
import FormSubmitStatus from '../forms/form-submit-status';

export const groupSchema = ({ editing }) => Yup.object().shape({
  name: Yup.string().nullable().required('Name is required'),
  slug: Yup.string().nullable(),
  description: Yup.string().nullable(),
  memberDescription: Yup.string().nullable(),
  public: Yup.boolean(),
  websiteUrl: Yup.string().nullable(),
  instagramUsername: Yup.string().nullable(),
  image: Yup.mixed().nullable(),
  requestPrompt: Yup.string().nullable(),
  createEventPermission: Yup.string().required('Create event permission is required'),
  createPostPermission: Yup.string().required('Create post permission is required'),
  uploadImagePermission: Yup.string().required('Upload image permission is required'),
  ...(editing ? null : { cityId: Yup.string().required('City is required') })
});

const useStyles = makeStyles((theme) => ({
  submitButtons: {
    margin: theme.spacing(1, 0),
  },
  image: {
    display: 'flex',
  },
  currentImage: {
    alignSelf: 'center',
  },
  imageField: {
    flexGrow: 1,
    '&:not(:first-child)': {
      marginLeft: theme.spacing(2),
    },
  },
}));

const GroupForm = (props) => {
  const { isSubmitting, submitCount, editing, values, disabled: formDisabled, group } = props
  const classes = useStyles();
  const disabled = formDisabled || isSubmitting;
  const forceTouched = submitCount > 0;
  return (
    <Form>
      <TextField
        name="name"
        label="Name *"
        disabled={disabled}
        touched={forceTouched}
      />
      {editing && (
        <TextField
          name="slug"
          label="URL Slug"
          helperText="Must contain only lower case letters and dashes (-)"
          disabled={disabled}
          touched={forceTouched}
        />
      )}
      <TextAreaField
        name="description"
        label="Public Description"
        disabled={disabled}
        touched={forceTouched}
        helperText={(
          <span>
            <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
              Markdown
            </MLink>
            {' '}is supported
          </span>
        )}
      />
      <TextAreaField
        name="memberDescription"
        label="Additional Members-only Description"
        disabled={disabled}
        touched={forceTouched}
        helperText={(
          <span>
            Only visible to people who are members of the group.
            {' '}
            <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
              Markdown
            </MLink>
            {' '}is supported
          </span>
        )}
      />
      <TextField
        name="websiteUrl"
        label="Website URL"
        disabled={disabled}
      />
      <TextField
        name="instagramUsername"
        label="Instagram Username"
        disabled={disabled}
        helperText={'Example: @everydayridescom'}
      />
      {!editing && (
        <CityField
          name="cityId"
          label="City"
          disabled={disabled}
          touched={forceTouched}
        />
      )}

      <Typography variant="h2">Image</Typography>
      <div className={classes.image}>
        {group && group.image && (
          <div className={classes.currentImage}>
            <ImageUpload record={group} size="80x60i" />
          </div>
        )}
        <div className={classes.imageField}>
          <FileField
            name="image"
            label="Image"
            disabled={disabled}
          />
        </div>
      </div>

      <Typography variant="h2">Meta</Typography>
      <RadioGroupField
        name="createEventPermission"
        label="Who can create events?"
        disabled={disabled}
        touched={forceTouched}
        options={[
          {
            label: 'Owners only',
            value: 'OWNER',
          },
          {
            label: 'Any group member',
            value: 'MEMBER',
          },
          {
            label: 'Any group member after admin approval',
            value: 'MEMBER_WITH_APPROVAL',
          },
        ]}
      />
      <RadioGroupField
        name="createPostPermission"
        label="Who can create posts?"
        disabled={disabled}
        touched={forceTouched}
        options={[
          {
            label: 'Owners only',
            value: 'OWNER',
          },
          {
            label: 'Any group member',
            value: 'MEMBER',
          },
        ]}
      />
      <RadioGroupField
        name="uploadImagePermission"
        label="Who can upload images to the gallery?"
        disabled={disabled}
        touched={forceTouched}
        options={[
          {
            label: 'Owners only',
            value: 'OWNER',
          },
          {
            label: 'Any group member',
            value: 'MEMBER',
          },
        ]}
      />
      <RadioGroupField
        name="public"
        label="Privacy"
        disabled={disabled}
        touched={forceTouched}
        options={[
          {
            label: 'Public (anyone can join)',
            value: true
          },
          {
            label: 'Private (by invite or request to join)',
            value: false
          }
        ]}
      />
      {!values.public && (
        <TextAreaField
          name="requestPrompt"
          label="Join Request Prompt"
          disabled={disabled}
          touched={forceTouched}
          helperText={(
            <span>
              <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
                Markdown
              </MLink>
              {' '}is supported
            </span>
          )}
        />
      )}
      <RadioGroupField
        name="discoverable"
        label="Visibility"
        disabled={disabled}
        touched={forceTouched}
        options={[
          {
            label: 'Discoverable (shows up in the groups list)',
            value: true
          },
          {
            label: 'Hidden (only people with the group link)',
            value: false
          }
        ]}
      />

      <div className={classes.submitButtons}>
        <Button
          type="submit"
          margin="normal"
          disabled={disabled}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
        <FormSubmitStatus {...props} />
      </div>
    </Form>
  );
};

GroupForm.propTypes = {
  isSubmitting: PropTypes.bool,
  submitCount: PropTypes.number,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  values: PropTypes.object,
  group: PropTypes.object
};

export default GroupForm;
